/**
 * useFeatureFlags — fetches the minimal public commerce flag snapshot
 * (`/api/features/public`). Used by Fase 1G UI components to decide
 * whether to render new v2 commerce buttons.
 *
 * Hard guarantees:
 *  - Never reads admin-only endpoints.
 *  - Defaults all flags to `false` on error / before first fetch (safe).
 *  - Returns `{ flags, loading, error }`.
 */
import { useEffect, useState } from 'react';

const DEFAULT_FLAGS = {
  ec_v2_checkout_enabled: false,
  broad_scan_checkout_enabled: false,
  referrals_enabled: false,
};

export function useFeatureFlags() {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        const res = await fetch('/api/features/public', {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setFlags({
            ec_v2_checkout_enabled: !!data?.ec_v2_checkout_enabled,
            broad_scan_checkout_enabled: !!data?.broad_scan_checkout_enabled,
            referrals_enabled: !!data?.referrals_enabled,
          });
        }
      } catch (err) {
        if (!cancelled) {
          // Fail-safe: hide all v2 buttons on error.
          setError(err);
          setFlags(DEFAULT_FLAGS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, []);

  return { flags, loading, error };
}
